import React, { useMemo, useState } from "react";
import { Box, IconButton } from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import XIconSvg from "../../../../common/svgs/XIconSvg";
import PrimaryButton from "../../../../common/ui/PrimaryButton";
import SecondaryButton from "../../../../common/ui/SecondaryButton";
import ScrollableWrapper from "../common/ScrollableWrapper";
import {
  PnoeMeasurementCatalogs,
  PnoePhase,
  PnoeProtocol,
  toNumber,
} from "../../entities/pnoeMeasurement";
import { getPhaseTypeLabel } from "../../utils/pnoeMeasurementStatus";

/**
 * "Edit phases" side panel (Figma node 16909:70672).
 *
 * A row per phase — its name, when it starts, and its load — since the phases
 * are only meaningful relative to each other: moving one boundary is really a
 * statement about the phase before and after it, so they are edited together
 * rather than one at a time.
 *
 * Which load columns appear follows the equipment: a treadmill test is speed
 * and grade, an ergometer test is watts. The design draws the treadmill case.
 *
 * A phase must stay inside its neighbours — see `validateDrafts`. The two
 * kinds of bad input are treated differently: a malformed *shape* (`113:44`)
 * is refused at the keystroke, while a well-formed time that collides with a
 * neighbour is accepted, kept exactly as typed, and reported under the cell,
 * with Save held until it is resolved. Nothing is silently corrected.
 */

/**
 * What a row holds.
 *
 * The start time is kept as **text**, not seconds: an entry that conflicts
 * with a neighbour has to stay on screen exactly as it was typed so the reader
 * can see what is being complained about. Storing a number would round-trip
 * `13:99` into `14:39` and silently change the value.
 */
interface PhaseDraft {
  elapsedText: string;
  watts: number | null;
  speed: number | null;
  grade: number | null;
}

export type PnoePhaseDrafts = Record<string, PhaseDraft>;

/**
 * `"6:30"` → 390 seconds. Null when the text is not a usable `mm:ss`.
 *
 * Seconds past 59 are rejected rather than carried into the minutes: `13:99`
 * is a typo, and turning it into `14:39` would be the silent correction this
 * field deliberately avoids.
 */
export const parseElapsed = (input: string): number | null => {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const parts = trimmed.split(":");
  if (parts.length === 1) {
    const minutes = Number(parts[0]);
    return Number.isNaN(minutes) ? null : Math.max(0, minutes * 60);
  }
  if (parts.length !== 2) return null;
  const m = Number(parts[0]);
  const sec = Number(parts[1]);
  if (parts[1] === "" || Number.isNaN(m) || Number.isNaN(sec)) return null;
  if (sec > 59) return null;
  return Math.max(0, m * 60 + sec);
};

/**
 * Keystroke filter for the start-time cell: at most two digits either side of
 * the separator.
 *
 * This is what stops a `113:44` from ever being typed — a malformed *shape* is
 * refused at the keystroke, while a well-formed time that merely conflicts
 * with a neighbour is accepted and flagged.
 */
export const isTypableElapsed = (text: string) =>
  /^\d{0,2}(:\d{0,2})?$/.test(text);

/** Seconds → `"06:30"`, zero-padded as the design shows it. */
const formatElapsedPadded = (seconds: number | null): string => {
  if (seconds === null) return "";
  const whole = Math.max(0, Math.round(seconds));
  return `${String(Math.floor(whole / 60)).padStart(2, "0")}:${String(
    whole % 60
  ).padStart(2, "0")}`;
};

/**
 * Row errors, keyed by phase id.
 *
 * A phase's start has to sit strictly after the phase before it and strictly
 * before the phase after it, or the two would swap order and a phase would
 * have no duration. A conflicting value is **kept and reported** rather than
 * corrected — the reader decides what it should be, not the field.
 */
export const validateDrafts = (
  phases: PnoePhase[],
  drafts: PnoePhaseDrafts
): Record<string, string> => {
  const errors: Record<string, string> = {};
  const secondsOf = (phase?: PnoePhase) =>
    phase ? parseElapsed(drafts[phase.id]?.elapsedText ?? "") : null;

  phases.forEach((phase, index) => {
    const value = secondsOf(phase);
    if (value === null) {
      errors[phase.id] = "Use mm:ss";
      return;
    }
    const before = secondsOf(phases[index - 1]);
    const after = secondsOf(phases[index + 1]);
    if (before !== null && value <= before) {
      errors[phase.id] = `Must start after ${formatElapsedPadded(before)}`;
      return;
    }
    if (after !== null && value >= after) {
      errors[phase.id] = `Must start before ${formatElapsedPadded(after)}`;
    }
  });
  return errors;
};

/** Treadmills are speed + grade; everything else reports watts. */
const usesWatts = (equipmentType: string | null | undefined) =>
  !/treadmill/i.test(String(equipmentType ?? ""));

interface PnoePhasesEditPanelProps {
  phases: PnoePhase[];
  protocol: PnoeProtocol;
  catalogs: PnoeMeasurementCatalogs | null;
  /** True for an RMR test, which has no load columns at all. */
  restingOnly: boolean;
  onClose: () => void;
  /**
   * The `PhaseBatchUpdate` body: only the phases whose values changed, keyed by
   * phase id, with the API's own field names. Times are in seconds — the
   * network layer serialises them as decimal strings.
   */
  onSave: (changed: Record<string, Record<string, number | null>>) => void;
  saving?: boolean;
}

const PnoePhasesEditPanel: React.FC<PnoePhasesEditPanelProps> = ({
  phases,
  protocol,
  catalogs,
  restingOnly,
  onClose,
  onSave,
  saving = false,
}) => {
  const theme: Theme = useTheme();

  const initial: PnoePhaseDrafts = useMemo(
    () =>
      phases.reduce<PnoePhaseDrafts>((acc, phase) => {
        acc[phase.id] = {
          elapsedText: formatElapsedPadded(toNumber(phase.elapsed_time)),
          watts: toNumber(phase.watts),
          speed: toNumber(phase.speed),
          grade: toNumber(phase.grade),
        };
        return acc;
      }, {}),
    [phases]
  );

  const [drafts, setDrafts] = useState<PnoePhaseDrafts>(initial);

  const set = (
    phaseId: string,
    field: keyof PhaseDraft,
    value: number | string | null
  ) =>
    setDrafts((previous) => ({
      ...previous,
      [phaseId]: { ...previous[phaseId], [field]: value },
    }));

  const errors = useMemo(
    () => validateDrafts(phases, drafts),
    [phases, drafts]
  );
  const hasErrors = Object.keys(errors).length > 0;


  // The batch PATCH body: only changed rows, and within a row only changed
  // fields. Re-sending an untouched value would round it to three decimals on
  // the way out and read as an edit, costing a report recalculation.
  //
  // The keys are the API's own (`elapsed_time`, not `elapsed`), so this object
  // is the request body rather than something the caller has to translate.
  const changed = useMemo(() => {
    const body: Record<string, Record<string, number | null>> = {};
    phases.forEach((phase) => {
      const before = initial[phase.id];
      const after = drafts[phase.id];
      if (!before || !after) return;
      const diff: Record<string, number | null> = {};
      // The start time is compared in seconds, so "3:43" and "03:43" are the
      // same value rather than a change.
      const beforeSeconds = parseElapsed(before.elapsedText);
      const afterSeconds = parseElapsed(after.elapsedText);
      if (afterSeconds !== null && afterSeconds !== beforeSeconds) {
        diff.elapsed_time = afterSeconds;
      }
      (["watts", "speed", "grade"] as const).forEach((field) => {
        if (after[field] !== before[field]) diff[field] = after[field];
      });
      if (Object.keys(diff).length) body[phase.id] = diff;
    });
    return body;
  }, [phases, initial, drafts]);

  const hasChanges = Object.keys(changed).length > 0;
  const watts = usesWatts(protocol?.amr_equipment_type);

  // Elapsed time is always editable; the load columns depend on the test.
  const columns = restingOnly
    ? []
    : watts
    ? [{ field: "watts" as const, label: "Power", unit: "W" }]
    : [
        { field: "speed" as const, label: "Power", unit: "km/h" },
        { field: "grade" as const, label: "Load", unit: "%" },
      ];

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerText}>
          <div style={styles.title(theme)}>Edit phases</div>
          <div style={styles.subtitle(theme)}>
            Applies to this measurement only. The client's profile stays
            unchanged.
          </div>
        </div>
        <div style={styles.iconWrapper} onClick={onClose} role="button">
          <XIconSvg />
        </div>
      </div>

      <div style={styles.body}>
        <ScrollableWrapper>
          <div style={styles.table(theme)}>
            <div style={styles.headRow(theme)}>
              <div style={styles.headCellName(theme)}>Phase</div>
              <div style={styles.headCell(theme)}>Elapsed time</div>
              {columns.map((column) => (
                <div key={column.field} style={styles.headCell(theme)}>
                  {column.label}
                </div>
              ))}
            </div>

            <div style={styles.rows}>
              {phases.map((phase, index) => {
                const draft = drafts[phase.id];
                const error = errors[phase.id];
                return (
                  <React.Fragment key={phase.id}>
                    {index > 0 && <div style={styles.rowDivider(theme)} />}
                    <div style={styles.row}>
                      <div style={styles.nameCell(theme)}>
                        {phase.name ||
                          getPhaseTypeLabel(phase.type, catalogs)}
                      </div>

                      <div style={styles.valueCell}>
                        <TimeCell
                          value={draft?.elapsedText ?? ""}
                          invalid={Boolean(error)}
                          onChange={(next) =>
                            set(phase.id, "elapsedText", next)
                          }
                        />
                        {error && (
                          <div style={styles.cellError(theme)}>{error}</div>
                        )}
                      </div>

                      {columns.map((column) => (
                        <div key={column.field} style={styles.valueCell}>
                          <NumberCell
                            value={draft?.[column.field] ?? null}
                            unit={column.unit}
                            onChange={(next) =>
                              set(phase.id, column.field, next)
                            }
                          />
                        </div>
                      ))}
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
          <div style={{ height: "24px" }} />
        </ScrollableWrapper>
      </div>

      <div style={styles.footer}>
        <SecondaryButton label="Cancel" onClick={onClose} />
        <PrimaryButton
          label="Save"
          onClick={() => onSave(changed)}
          isDisable={!hasChanges || hasErrors || saving}
        />
      </div>
    </div>
  );
};

export default PnoePhasesEditPanel;

/**
 * The `mm:ss` start-time cell, with scale arrows stepping one second.
 *
 * Not `NumericInputFieldScale`: that field is a 56px floating-label box over a
 * plain number, and this is a 30px unlabelled table cell holding `06:30` — the
 * column header does the labelling. The arrows are correspondingly smaller so
 * the pair fits the cell's height.
 *
 * Fully controlled by its text, with nothing rewritten behind the reader:
 * what they type is what stays, and a conflict is reported under the cell
 * rather than corrected.
 */
const TimeCell: React.FC<{
  value: string;
  invalid: boolean;
  onChange: (value: string) => void;
}> = ({ value, invalid, onChange }) => {
  const theme: Theme = useTheme();
  const seconds = parseElapsed(value);

  // Stepping an unusable entry would have to invent a starting point, so the
  // arrows wait until the text parses. They step freely past a neighbour: an
  // explicit press is the reader's decision, and the row says what is wrong.
  const step = (delta: number) => {
    if (seconds === null) return;
    onChange(formatElapsedPadded(Math.max(0, seconds + delta)));
  };

  return (
    <Box sx={styles.cell(theme, invalid)}>
      <Box
        component="input"
        sx={styles.cellInput(theme)}
        value={value}
        inputMode="numeric"
        aria-label="Elapsed time"
        aria-invalid={invalid}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          // Only a well-formed shape gets in — two digits either side of the
          // separator — so `113:44` cannot be typed. A time that merely
          // collides with a neighbour is accepted and reported.
          const next = event.target.value;
          if (!isTypableElapsed(next)) return;
          onChange(next);
        }}
      />
      <Box sx={styles.cellArrows}>
        <IconButton
          aria-label="increase elapsed time"
          onClick={() => step(1)}
          disabled={seconds === null}
          sx={styles.cellArrowButton(theme)}
        >
          <KeyboardArrowUpIcon sx={styles.cellArrowIcon} />
        </IconButton>
        <IconButton
          aria-label="decrease elapsed time"
          onClick={() => step(-1)}
          disabled={seconds === null || seconds <= 0}
          sx={styles.cellArrowButton(theme)}
        >
          <KeyboardArrowDownIcon sx={styles.cellArrowIcon} />
        </IconButton>
      </Box>
    </Box>
  );
};

/** A plain numeric cell with its unit shown after the value, as the design has it. */
const NumberCell: React.FC<{
  value: number | null;
  unit: string;
  onChange: (value: number | null) => void;
}> = ({ value, unit, onChange }) => {
  const theme: Theme = useTheme();
  return (
    <Box sx={styles.cell(theme, false)}>
      <Box
        component="input"
        sx={styles.cellInput(theme)}
        value={value === null ? "" : String(value)}
        inputMode="decimal"
        aria-label={unit}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          const raw = event.target.value;
          if (raw === "") return onChange(null);
          const parsed = Number(raw);
          if (!Number.isNaN(parsed)) onChange(parsed);
        }}
      />
      <Box sx={styles.cellUnit(theme)}>{unit}</Box>
    </Box>
  );
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column" as "column",
    height: "100%",
    width: "100%",
    padding: "16px",
    boxSizing: "border-box" as "border-box",
    gap: "32px",
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    gap: "16px",
    flexShrink: 0,
  },
  headerText: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "4px",
    flex: 1,
    minWidth: 0,
  },
  title: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "20px",
    lineHeight: "1.4",
    letterSpacing: "-0.4px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  subtitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "1.4",
    letterSpacing: "-0.24px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  iconWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "8px",
    borderRadius: "4px",
    cursor: "pointer",
    flexShrink: 0,
  },
  body: {
    display: "flex",
    flexDirection: "column" as "column",
    flex: 1,
    minHeight: 0,
    overflow: "hidden" as "hidden",
  },
  table: (theme: Theme) => ({
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    // `overflow: hidden` rounds the header's corners, but it also means that
    // if the flex parent shrinks this box the extra rows are simply clipped
    // away instead of scrolling. `flexShrink: 0` keeps its natural height so
    // the ScrollableWrapper above is what overflows.
    overflow: "hidden" as "hidden",
    flexShrink: 0,
    backgroundColor: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
  }),
  headRow: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    gap: "16px",
    padding: "4px 12px",
    backgroundColor: theme.colorTokens.N[40],
  }),
  headCellName: (theme: Theme) => ({
    flex: "1 1 0",
    minWidth: 0,
    fontFamily: "var(--font-Medium)",
    fontSize: "10px",
    lineHeight: "18.24px",
    letterSpacing: "1.03px",
    textTransform: "capitalize" as "capitalize",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  headCell: (theme: Theme) => ({
    width: "96px",
    flexShrink: 0,
    fontFamily: "var(--font-Medium)",
    fontSize: "10px",
    lineHeight: "18.24px",
    letterSpacing: "1.03px",
    textTransform: "capitalize" as "capitalize",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  rows: {
    display: "flex",
    flexDirection: "column" as "column",
    padding: "12px",
    gap: "8px",
  },
  rowDivider: (theme: Theme) => ({
    height: "1px",
    backgroundColor: theme.colors_ds.NEUTRAL_BORDER_WEAK,
  }),
  row: {
    display: "flex",
    alignItems: "flex-start",
    gap: "16px",
    minHeight: "35px",
  },
  nameCell: (theme: Theme) => ({
    flex: "1 1 0",
    minWidth: 0,
    paddingTop: "8px",
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "1.4",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  valueCell: {
    width: "96px",
    flexShrink: 0,
    display: "flex",
    flexDirection: "column" as "column",
    gap: "2px",
  },
  cell: (theme: Theme, invalid: boolean) => ({
    display: "flex",
    alignItems: "center",
    gap: "4px",
    height: "30px",
    padding: "0 4px 0 8px",
    borderRadius: "8px",
    backgroundColor: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
    border: `1px solid ${
      invalid
        ? theme.colorTokens.D[70]
        : theme.colors_ds.NEUTRAL_BORDER_WEAK
    }`,
  }),
  cellInput: (theme: Theme) => ({
    flex: 1,
    minWidth: 0,
    border: "none",
    outline: "none",
    padding: 0,
    backgroundColor: "transparent",
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "1.4",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  cellUnit: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    flexShrink: 0,
    paddingRight: "4px",
  }),
  // 13px chevrons rather than the 21px of the standalone field: stacked, they
  // have to fit a 30px table cell.
  cellArrows: {
    display: "flex",
    flexDirection: "column" as "column",
    flexShrink: 0,
  },
  cellArrowButton: (theme: Theme) => ({
    padding: 0,
    width: "13px",
    height: "13px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    "&:hover": { color: theme.colors_ds.PRIMARY_ICON_MAIN },
  }),
  cellArrowIcon: {
    fontSize: "13px",
    color: "inherit",
  },
  cellError: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "10px",
    lineHeight: "1.3",
    color: theme.colorTokens.D[70],
  }),
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "16px",
    flexShrink: 0,
  },
};
